import { type Country, useCountries, useFormatAmount, useIntl } from '@capsicum/common';
import { EditIcon, VanIcon } from '@capsicum/common-web/icons';
import styled, { useTheme } from 'styled-components';

import { ShippingFlag } from '../shipping-flag/ShippingFlag';

export type ShippingCostProps = {
	chzCurrencyShortCode: string;
	chzRate: number;
	fiatCurrencyShortCode: string;
	iso2Country: string | undefined;
	onClick: () => void;
	shippingCost: number;
	testID?: string;
};

export const ShippingCost = ({
	chzCurrencyShortCode,
	chzRate,
	fiatCurrencyShortCode,
	iso2Country,
	onClick,
	shippingCost,
	testID
}: ShippingCostProps) => {
	const { getCountryBy, getCountryFlagUri } = useCountries();
	const { $t } = useIntl();
	const { colors } = useTheme();
	const { formatAmount } = useFormatAmount();

	const country = getCountryBy(iso2Country?.toUpperCase(), 'iso2') as Country;
	const countryName = country?.translations?.[0]?.value || '';

	return (
		<S.Layout data-testid={testID}>
			<S.IconsContainer>
				<VanIcon color={colors.icons.main.enabled} />
				<S.ShippingContainer>
					<ShippingFlag url={getCountryFlagUri(country)} />
				</S.ShippingContainer>
				<S.TextContent>
					<S.StyledText>
						{$t('rewards.auctions.shipping', {
							country: countryName,
							shippingCost: `${formatAmount(shippingCost, {
								maximumFractionDigits: 2
							})} ${fiatCurrencyShortCode}`,
							shippingCostCHZ: `${formatAmount(shippingCost / chzRate, {
								maximumFractionDigits: 2,
								minimumFractionDigits: 2
							})} ${chzCurrencyShortCode}`
						})}
					</S.StyledText>
				</S.TextContent>
			</S.IconsContainer>
			<S.EditContainer onClick={onClick}>
				<EditIcon color={colors.icons.main.enabled} />
			</S.EditContainer>
		</S.Layout>
	);
};

namespace S {
	export const Layout = styled.div`
		display: flex;
		padding: ${({ theme }) => theme.spacings.small};

		${({ theme }) => theme.border.radius.medium};
		border: solid;
		border-width: ${({ theme }) => theme.border.width.small};
		border-color: ${({ theme }) => theme.colors.borders.main.divider};

		flex-direction: row;
		align-items: center;
		justify-content: space-between;
	`;

	export const TextContent = styled.div`
		text-align: left;
		flex-shrink: 1;
		padding-right: ${({ theme }) => theme.spacings.small};
		padding-left: ${({ theme }) => theme.spacings.small};
	`;

	export const IconsContainer = styled.div`
		display: flex;
		flex-direction: row;
		align-items: center;
	`;

	export const ShippingContainer = styled.div`
		margin-left: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const EditContainer = styled.div`
		cursor: pointer;
	`;

	export const StyledText = styled.div`
		${({ theme }) => theme.fonts.text.text5};
		white-space: pre-wrap;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const SecondaryText = styled.div`
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
}
